import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useClientPortalPaths } from "../../hooks/useClientPortalBase";
import { getClientCommunity } from "../../api/clientCommunitiesApi";
import { listClientCourses } from "../../api/clientCoursesApi";
import {
  ClientPortalError,
  ClientPortalLoading,
} from "../../components/clientPortal/ClientPortalStates";
import { formatClientDateTime, formatStatusLabel } from "../../utils/clientPortalFormat";

const ClientCommunityDetailPage = () => {
  const { clientViewId, communityId } = useParams();
  const [community, setCommunity] = useState(null);
  const [relatedCourses, setRelatedCourses] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [canManageCourses, setCanManageCourses] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const paths = useClientPortalPaths();

  useEffect(() => {
    Promise.all([
      getClientCommunity(clientViewId, communityId),
      listClientCourses(clientViewId, { communityId, pageSize: 10 }).catch(() => ({
        courses: [],
        canManage: false,
      })),
    ])
      .then(([communityBody, coursesBody]) => {
        setCommunity(communityBody.community);
        setCanManage(communityBody.canManage);
        setRelatedCourses(coursesBody.courses || []);
        setCanManageCourses(coursesBody.canManage);
      })
      .catch((error) => {
        setErrorMessage(error.message || "Failed to load community.");
      })
      .finally(() => setLoading(false));
  }, [clientViewId, communityId]);

  if (loading) {
    return <ClientPortalLoading message="Loading community…" />;
  }

  if (errorMessage) {
    return <ClientPortalError message={errorMessage} />;
  }

  if (canManage) {
    return <Navigate to={paths.viewSection(clientViewId, "communities", communityId, "edit")} replace />;
  }

  if (!community) {
    return <ClientPortalError message="Community not found." />;
  }

  return (
    <section className="panel-card">
      <p className="eyebrow">
        <Link to={paths.viewSection(clientViewId, "communities")}>Communities</Link> / {community.communityName}
      </p>
      <h2>{community.communityName}</h2>
      <p className="muted">
        {formatStatusLabel(community.displayStatus)} · Updated{" "}
        {formatClientDateTime(community.updatedAt)}
      </p>
      <p>{community.shortDescription || community.longDescription}</p>

      {relatedCourses.length ? (
        <div className="client-community-related-courses">
          <h3>Related courses</h3>
          <ul>
            {relatedCourses.map((course) => (
              <li key={course.courseId}>
                <strong>{course.courseName}</strong>
                <span className="muted"> · {formatStatusLabel(course.displayStatus)}</span>
                {(course.numberOfHoles || course.par) && (
                  <span className="muted">
                    {" "}
                    · {course.numberOfHoles ? `${course.numberOfHoles} holes` : ""}
                    {course.par ? ` par ${course.par}` : ""}
                  </span>
                )}
                {" · "}
                {canManageCourses ? (
                  <Link to={paths.viewSection(clientViewId, "courses", course.courseId, "edit")}>
                    Edit
                  </Link>
                ) : (
                  <Link to={paths.viewSection(clientViewId, "courses", course.courseId)}>View</Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
};

export default ClientCommunityDetailPage;
